import { Defaults } from "@common/_constants/";
import { Button } from "@common/components/Atoms/Button";
import EditReferralError from "@common/components/Atoms/Error";
import { FormHandler, Input, Rules } from "@common/components/Atoms/FormInputs";
import { IconClose } from "@common/components/Atoms/Icons";
import FileDrop from "@common/components/Molecules/FileDrop";
import Modal from "@common/components/Molecules/Modal";
import { File as FileModel, FormField } from "@common/models";
import { ReferralService } from "@pro/_services";
import { cloneDeep } from "lodash";
import React from "react";

interface EditReferralProps {
    isOpen: boolean;
    onOkay: () => void;
    onCancel: () => void;
    closeModal: () => void;
    jobCandidateId: number;
}

interface EditJobCandidateModel {
    job_id: number;
    job_candidate_id: number;
    job_name: string;
    resume_file: FileModel;
    user_full_name: string;
    attachments: FileModel[];
    user_email: string;
    user_phone_number: string;
}

interface EditReferralState {
    error: boolean;
    errorMessages: string[];
    resume: {
        isValid: boolean;
        resumeFileId?: number;
        resumeFile?: FileModel;
        originalResumeFile?: FileModel;
        validationMessages: string[];
    };

    originalJobCandidate?: EditJobCandidateModel;
    attachments: {
        isValid: boolean;
        originalFiles: FileModel[];
        newFileIds: number[];
        validationMessages: string[];
    };
    formIsValid: boolean;
    formFields: {
        name: FormField;
        email: FormField;
        phone: FormField;
    };
    processing: boolean;
}

export class EditReferral extends React.Component<
    EditReferralProps,
    EditReferralState
> {
    private _abortControllers: { [key: string]: AbortController } = {};

    constructor(props) {
        super(props);

        this.emptyState = this.emptyState.bind(this);
        this.initialStateForCandidate = this.initialStateForCandidate.bind(
            this
        );
        this.getReferral = this.getReferral.bind(this);
        this.removeAttachment = this.removeAttachment.bind(this);

        this.onSubmit = this.onSubmit.bind(this);
        this.onCloseModal = this.onCloseModal.bind(this);
        this.onFormFieldChange = this.onFormFieldChange.bind(this);

        this.handleResumeFileAdd = this.handleResumeFileAdd.bind(this);
        this.handleResumeFileRemove = this.handleResumeFileRemove.bind(this);
        this.handleResumeFileUploading = this.handleResumeFileUploading.bind(
            this
        );
        this.handleResumeFileUploaded = this.handleResumeFileUploaded.bind(
            this
        );
        this.handleResumeFileError = this.handleResumeFileError.bind(this);

        this.handleAttachmentFilesAdd = this.handleAttachmentFilesAdd.bind(
            this
        );
        this.handleAttachmentFilesRemove = this.handleAttachmentFilesRemove.bind(
            this
        );
        this.handleAttachmentFilesUploading = this.handleAttachmentFilesUploading.bind(
            this
        );
        this.handleAttachmentFilesUploaded = this.handleAttachmentFilesUploaded.bind(
            this
        );
        this.handleAttachmentFilesError = this.handleAttachmentFilesError.bind(
            this
        );

        this.state = this.emptyState();
    }

    componentDidMount() {
        if (this.props.isOpen) {
            this.getReferral(this.props.jobCandidateId);
        }
    }

    componentWillReceiveProps(newProps: EditReferralProps) {
        if (
            newProps.isOpen &&
            (newProps.isOpen !== this.props.isOpen ||
                newProps.jobCandidateId !== this.props.jobCandidateId)
        ) {
            this.getReferral(newProps.jobCandidateId);
        }
    }

    componentWillUnmount() {
        const abortControllers = this._abortControllers;
        if (Object.keys(abortControllers).length > 0) {
            Object.keys(abortControllers).map(key =>
                abortControllers[key].abort()
            );
        }
    }

    emptyState(): EditReferralState {
        return {
            error: false,
            errorMessages: [],
            resume: {
                isValid: false,
                resumeFileId: undefined,
                resumeFile: undefined,
                originalResumeFile: undefined,
                validationMessages: [],
            },
            originalJobCandidate: undefined,
            attachments: {
                isValid: false,
                originalFiles: [],
                newFileIds: [],
                validationMessages: [],
            },
            formIsValid: true,
            formFields: {
                name: {
                    name: "name",
                    value: "",
                    label: "",
                    isValid: false,
                    validationMessages: [],
                    rules: [Rules.required],
                },
                email: {
                    name: "email",
                    value: "",
                    label: "",
                    isValid: false,
                    validationMessages: [],
                    rules: [Rules.required, Rules.formatEmail],
                },
                phone: {
                    name: "phone",
                    value: "",
                    label: "",
                    isValid: false,
                    validationMessages: [],
                    rules: [Rules.required, Rules.formatPhone],
                },
            },
            processing: true,
        };
    }

    initialStateForCandidate(
        jobCandidate: EditJobCandidateModel
    ): EditReferralState {
        return {
            error: false,
            errorMessages: [],
            resume: {
                isValid: true,
                resumeFileId: jobCandidate.resume_file
                    ? jobCandidate.resume_file.file_id
                    : 0,
                resumeFile: jobCandidate.resume_file,
                originalResumeFile: jobCandidate.resume_file,
                validationMessages: [],
            },
            originalJobCandidate: jobCandidate,
            attachments: {
                isValid: true,
                originalFiles: [...jobCandidate.attachments],
                newFileIds: [],
                validationMessages: [],
            },

            formIsValid: true,
            formFields: {
                name: {
                    name: "name",
                    label: "",
                    value: jobCandidate.user_full_name,
                    isValid: true,
                    validationMessages: [],
                    rules: [Rules.required],
                },
                email: {
                    name: "email",
                    label: "",
                    value: jobCandidate.user_email,
                    isValid: true,
                    validationMessages: [],
                    rules: [Rules.required, Rules.formatEmail],
                },
                phone: {
                    name: "phone",
                    label: "",
                    value: jobCandidate.user_phone_number,
                    isValid: true,
                    validationMessages: [],
                    rules: [Rules.required, Rules.formatPhone],
                },
            },
            processing: false,
        };
    }

    getReferral(jobCandidateId: number) {
        // Check to see if there already was an abortController, and if so, call abort on it
        if (this._abortControllers.getReferral) {
            this._abortControllers.getReferral.abort();
        }
        this._abortControllers.getReferral = new AbortController();
        const signal = this._abortControllers.getReferral.signal;

        ReferralService.getReferral(jobCandidateId, signal).then(candidate =>
            this.setState(this.initialStateForCandidate(candidate))
        );
    }

    removeAttachment(index: number) {
        if (index < 0 || index > this.state.attachments.originalFiles.length) {
            return;
        }

        this.setState(prevState => {
            const atts = prevState.attachments.originalFiles;
            const newAtts = [
                ...atts.slice(0, index),
                ...atts.slice(index + 1, atts.length),
            ];
            return {
                attachments: {
                    ...prevState.attachments,
                    originalFiles: newAtts,
                },
            };
        });
    }

    onFormFieldChange(name: string, value: string) {
        const formFields = this.state.formFields;
        FormHandler.handleInputChange(name, value, formFields);
        this.setState({ formFields });
    }

    onSubmit(event: any) {
        event.preventDefault();

        const formFields = this.state.formFields;
        const isValid =
            FormHandler.validate(formFields) &&
            !!this.state.resume.resumeFileId;

        if (!isValid) {
            this.setState(prevState => {
                if (!prevState.resume.resumeFileId) {
                    const resume = {
                        ...prevState.resume,
                        isValid: false,
                        validationMessages: ["A resume upload is required"],
                    };
                    return { formFields, formIsValid: false, resume };
                }
                return {
                    formFields,
                    formIsValid: false,
                    resume: prevState.resume,
                };
            });
        } else {
            this.setState(
                { formFields, formIsValid: true, processing: true },
                () => {
                    const orig = this.state.originalJobCandidate;
                    const name = splitName(this.state.formFields.name.value);
                    const jobCandidate = {
                        job_id: orig!.job_id,
                        job_candidate_id: orig!.job_candidate_id,
                        candidate_first_name: name[0],
                        candidate_last_name: name[1],
                        candidate_email: this.state.formFields.email.value,
                        candidate_phone: this.state.formFields.phone.value,
                        candidate_resume_file_id: this.state.resume
                            .resumeFileId,
                        attachment_file_ids: this.state.attachments.originalFiles
                            .map(att => att.file_id)
                            .concat(this.state.attachments.newFileIds),
                    };

                    ReferralService.update(jobCandidate)
                        .then(this.props.onOkay)
                        .catch(err => handleError(err, this));
                }
            );
        }
    }

    onCloseModal() {
        this.setState(this.emptyState(), this.props.closeModal);
    }

    handleResumeFileAdd(fileId: number) {
        const resume = {
            ...this.state.resume,
            resumeFileId: fileId,
            isValid: true,
        };
        this.setState({ resume });
    }

    handleResumeFileRemove(fileId: number) {
        const original = this.state.resume.originalResumeFile;
        if (original) {
            const resume = {
                ...this.state.resume,
                resumeFileId: original.file_id,
            };
            this.setState({ resume });
        } else {
            const resume = { ...this.state.resume, resumeFileId: undefined };
            this.setState({ resume });
        }
    }

    handleResumeFileUploading() {
        const resume = {
            ...this.state.resume,
            validationMessages: [],
            isValid: true,
        };
        this.setState({ resume, processing: true });
    }

    handleResumeFileUploaded() {
        this.setState({ processing: false });
    }

    handleResumeFileError(errorMsg: string) {
        const resume = { ...this.state.resume, isValid: false };
        resume.validationMessages.push(errorMsg);
        this.setState({ resume });
    }

    handleAttachmentFilesAdd(fileId) {
        const atts = cloneDeep(this.state.attachments.newFileIds);
        atts.push(fileId);

        const attachments = {
            ...this.state.attachments,
            newFileIds: (atts as unknown) as any[],
        };
        this.setState({ attachments });
    }

    handleAttachmentFilesRemove(fileId) {
        const atts = cloneDeep(this.state.attachments.newFileIds).filter(
            a => a !== fileId
        );

        const attachments = {
            ...this.state.attachments,
            newFileIds: (atts as unknown) as any[],
        };
        this.setState({ attachments });
    }

    handleAttachmentFilesUploading() {
        const attachments = {
            ...this.state.attachments,
            validationMessages: [],
            isValid: true,
        };
        this.setState({ attachments, processing: true });
    }

    handleAttachmentFilesUploaded() {
        this.setState({ processing: false });
    }

    handleAttachmentFilesError(errorMsg) {
        const attachments = { ...this.state.attachments, isValid: false };
        attachments.validationMessages.push(errorMsg);
        this.setState({ attachments });
    }

    render() {
        const jobCandidate = this.state.originalJobCandidate;
        return (
            <Modal
                size="xs"
                title={jobCandidate === undefined ? "" : jobCandidate.job_name}
                isOpen={this.props.isOpen}
                closeModal={this.onCloseModal}
                loading={this.state.processing}
            >
                <div className="c-block">
                    <h1 className="c-block__title">edit referral</h1>
                </div>

                <form onSubmit={this.onSubmit}>
                    <Input
                        type="text"
                        name="name"
                        label="name"
                        value={this.state.formFields.name.value}
                        isValid={this.state.formFields.name.isValid}
                        validationMessages={
                            this.state.formFields.name.validationMessages
                        }
                        onChange={this.onFormFieldChange}
                    />

                    <Input
                        type="text"
                        name="email"
                        label="email"
                        value={this.state.formFields.email.value}
                        isValid={this.state.formFields.email.isValid}
                        validationMessages={
                            this.state.formFields.email.validationMessages
                        }
                        onChange={this.onFormFieldChange}
                    />

                    <Input
                        type="tel"
                        name="phone"
                        label="phone"
                        value={this.state.formFields.phone.value}
                        isValid={this.state.formFields.phone.isValid}
                        validationMessages={
                            this.state.formFields.phone.validationMessages
                        }
                        onChange={this.onFormFieldChange}
                    />

                    <div className="c-block">
                        <div>
                            <div className="c-control__label">
                                <label htmlFor="resumeDropZone">resume</label>
                            </div>
                            <div className="c-control__wrapper">
                                <FileDrop
                                    name="resumeDropZone"
                                    isMultiple={false}
                                    handleAdd={this.handleResumeFileAdd}
                                    handleRemove={this.handleResumeFileRemove}
                                    onFileUploading={
                                        this.handleResumeFileUploading
                                    }
                                    onFileUploaded={
                                        this.handleResumeFileUploaded
                                    }
                                    onFileError={this.handleResumeFileError}
                                    isValid={this.state.resume.isValid}
                                    validationMessages={
                                        this.state.resume.validationMessages
                                    }
                                    dropMessage="drop resume here to upload"
                                    fileCategory={
                                        Defaults.FileCategoryType.OriginalResume
                                    }
                                    acceptedFiles={
                                        ".doc,.docx,.pdf,.rtf,.txt,.rtf,.html,.htm"
                                    }
                                />
                            </div>
                        </div>
                    </div>

                    {this.state.resume.originalResumeFile &&
                        this.state.resume.resumeFileId ===
                            this.state.resume.originalResumeFile.file_id && (
                            <div className="c-block">
                                <ul className="c-list">
                                    <li>
                                        {
                                            this.state.resume.originalResumeFile
                                                .file_name
                                        }
                                    </li>
                                </ul>
                            </div>
                        )}

                    <div className="c-block">
                        <div className="c-control c-control--filedrop">
                            <div className="c-control__label">
                                <label htmlFor="attachmentDropZone">
                                    attachments
                                </label>
                            </div>
                            <div className="c-control__wrapper">
                                <FileDrop
                                    name="attachmentDropZone"
                                    isMultiple={true}
                                    handleAdd={this.handleAttachmentFilesAdd}
                                    handleRemove={
                                        this.handleAttachmentFilesRemove
                                    }
                                    onFileUploading={
                                        this.handleAttachmentFilesUploading
                                    }
                                    onFileUploaded={
                                        this.handleAttachmentFilesUploaded
                                    }
                                    onFileError={
                                        this.handleAttachmentFilesError
                                    }
                                    isValid={this.state.attachments.isValid}
                                    fileCategory={
                                        Defaults.FileCategoryType.Attachment
                                    }
                                    validationMessages={
                                        this.state.attachments
                                            .validationMessages
                                    }
                                />
                            </div>
                        </div>
                    </div>

                    {this.state.attachments.originalFiles.length > 0 && (
                        <div className="c-block">
                            <ul className="c-list">
                                {this.state.attachments.originalFiles.map(
                                    (att, i) => (
                                        <li key={i}>
                                            {att.file_name}
                                            <button
                                                className="c-control__action"
                                                type="button"
                                                onClick={() =>
                                                    this.removeAttachment(i)
                                                }
                                            >
                                                <IconClose />
                                            </button>
                                        </li>
                                    )
                                )}
                            </ul>
                        </div>
                    )}

                    <div className="c-block">
                        <EditReferralError
                            show={!this.state.formIsValid || this.state.error}
                            description={
                                this.state.errorMessages &&
                                this.state.errorMessages.length
                                    ? this.state.errorMessages[0]
                                    : ""
                            }
                        />
                    </div>

                    <div className="c-block">
                        <Button
                            id="edit-referral-save-button"
                            block={true}
                            green={true}
                            type="submit"
                            onClick={this.onSubmit}
                        >
                            save
                        </Button>
                    </div>
                </form>
            </Modal>
        );
    }
}

// Helpers...

function splitName(name) {
    if (!name) {
        throw new Error("Name must be a non-empty string");
    }

    const lastSpaceIndex = name.lastIndexOf(" ");
    if (lastSpaceIndex === -1) {
        return [name, ""];
    }

    return [
        name.substring(0, lastSpaceIndex),
        name.substring(lastSpaceIndex + 1, name.length),
    ];
}

function handleError(err, stateContainer) {
    const messages = err.responseData
        ? err.responseData.error_messages
        : [err.message];

    console.log(messages);
    stateContainer.setState({
        processing: false,
        error: true,
        errorMessages: messages,
    });
}
